/**
 * Save on Set - Cart Page Functionality  
 * Checks cart items for set discounts and displays messages
 * Discounts are applied automatically via Shopify Function
 */

class CartPageSets {
  constructor() {
    this.init();
  }

  init() {
    this.messageContainer = document.querySelector('.save-on-set-cart-messages');
    
    if (!this.messageContainer) {
      setTimeout(() => this.init(), 500);
      return;
    }

    this.checkForSetDiscounts();
    this.setupEventListeners();
  }

  setupEventListeners() {
    document.addEventListener('shopify:section:load', () => {
      setTimeout(() => this.init(), 300);
    });

    const cartElement = document.querySelector('cart-items');
    if (cartElement) {
      const observer = new MutationObserver(() => {
        const containerExists = document.querySelector('.save-on-set-cart-messages');
        if (containerExists) {
          setTimeout(() => this.checkForSetDiscounts(), 500);
        } else {
          setTimeout(() => this.init(), 500);
        }
      });

      observer.observe(cartElement, {
        childList: true,
        subtree: true
      });
    }
  }

  async checkForSetDiscounts() {
    if (this._checkingSets) return;
    this._checkingSets = true;

    this.messageContainer = document.querySelector('.save-on-set-cart-messages');
    if (!this.messageContainer) {
      this._checkingSets = false;
      return;
    }

    try {
      const response = await fetch('/cart.js', { cache: "no-store" });
      if (!response.ok) throw new Error("Cart request failed");
      const cart = await response.json();

      const setGroups = {};
      
      cart.items.forEach((item) => {
        const setId = item.properties?._set_id;
        const setName = item.properties?._set_name;
        
        if (setId) {
          let requiredProductIds = [];
          const gidsString = item.properties._set_products_gids;
          
          if (gidsString) {
            const gids = gidsString.split('||');
            requiredProductIds = gids.map(gid => {
              const parts = gid.split('/');
              return parts[parts.length - 1];
            });
          }
          
          if (!setGroups[setId]) {
            setGroups[setId] = {
              items: [],
              setName: setName || '',
              discountType: item.properties._discount_type,
              discountValue: parseFloat(item.properties._discount_value),
              badgeText: item.properties._badge_text,
              requiredProducts: requiredProductIds
            };
          }
          setGroups[setId].items.push(item);
        }
      });

      const messages = [];
      
      for (const [setId, setData] of Object.entries(setGroups)) {
        const itemProductIds = setData.items.map(item => item.product_id.toString());
        const requiredProductIds = setData.requiredProducts;
        const missingProducts = requiredProductIds.filter(id => !itemProductIds.includes(id));
        
        if (missingProducts.length === 0) {
          const quantities = {};
          setData.items.forEach(item => {
            quantities[item.product_id] = item.quantity;
          });
          
          const completeSets = Math.min(...Object.values(quantities));
          
          const pricePerSet = setData.items.reduce((sum, item) => {
            return sum + item.price;
          }, 0);
          
          let discountPerSet;
          if (setData.discountType === 'Percentage') {
            discountPerSet = pricePerSet * (setData.discountValue / 100);
          } else {
            discountPerSet = setData.discountValue * 100;
          }
          
          const totalDiscount = discountPerSet * completeSets;
          
          const formattedDiscount = this.formatMoney(totalDiscount);
          const formattedDiscountValue = setData.discountType === 'Percentage' 
            ? `${setData.discountValue}%` 
            : this.formatMoney(setData.discountValue * 100);
          
          const hasExtraItems = Object.values(quantities).some(qty => qty > completeSets);
          
          let message = `
            <div class="set-discount-applied">
              <strong>✅ ${setData.badgeText || 'Set Complete'} (${setData.setName || 'Exclusive Bundle'})</strong>
              <p>You're saving ${formattedDiscount} (${formattedDiscountValue} off ${completeSets} set${completeSets > 1 ? 's' : ''}) at checkout!</p>
            </div>
          `;
          
          if (hasExtraItems) {
            const extraItems = [];
            setData.items.forEach(item => {
              const extra = item.quantity - completeSets;
              if (extra > 0) {
                extraItems.push(`${extra}× ${item.product_title}`);
              }
            });
            
            message = `
              <div class="set-discount-incomplete">
                <strong>⚠️ ${setData.badgeText || 'Complete the Set'} (${setData.setName || 'Exclusive Bundle'})</strong>
                <p class="set-extra-items"><small>Extra items (${extraItems.join(', ')}) - Set discount will not be applied unless quantities match.</small></p>
              </div>
            `;
          }
          
          messages.push(message);
        } else {
          const itemCount = setData.items.length;
          const totalCount = requiredProductIds.length;
          const remaining = missingProducts.length;
          
          messages.push(`
            <div class="set-discount-incomplete">
              <strong>⚠️ ${setData.badgeText || 'Complete the Set'} (${setData.setName || 'Exclusive Bundle'})</strong>
              <p>You have ${itemCount} of ${totalCount} items in this set. Add ${remaining} more item${remaining > 1 ? 's' : ''} to save!</p>
            </div>
          `);
        }
      }

      if (this.messageContainer) {
        const newHTML = messages.join('');
        if (this.messageContainer.innerHTML !== newHTML) {
          this.messageContainer.innerHTML = newHTML;
        }
      }

    } catch (error) {
      console.error('Error checking set discounts:', error);
    }
    this._checkingSets = false;
  }

  formatMoney(cents) {
    return `$${(cents / 100).toFixed(2)}`;
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new CartPageSets());
} else {
  new CartPageSets();
}